import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { Canvas, RootState } from '@react-three/fiber'
import { CameraControls, OrthographicCamera, PerspectiveCamera } from '@react-three/drei'
import create from 'zustand'
import CameraControlsEnum from 'camera-controls'
import { useAppSelector, useAppDispatch } from '~/store/hooks'
import store, { RootState as AppRootState } from '~/store'
import {
  Box3,
  Object3D,
  OrthographicCamera as OrthographicCamera3,
  PerspectiveCamera as PerspectiveCamera3,
  Vector3,
} from 'three'
import {
  set2DPosition,
  set2DZoom,
  set3DPosition,
  set3DRotation,
  fitFinished,
  zoomIn,
  zoomOut,
  start2DFit,
} from '~/store/camera'
import { MountingStructures } from './MountingStructures'
import { Lights } from '~/components/DrawingCanvas/Lights'
import { Grid } from '~/components/DrawingCanvas/Grid'
import { Doors } from './Doors'
import { ToolManager, ToolManagerRef } from './ToolManager'
import { BackgroundImage } from '~/components/DrawingCanvas/BackgroundImage'
import { DimensionLabels } from './DimensionLabels'
import { CFD } from './CFD'
import { Ceilings } from './Ceilings'
import { Products } from '~/components/DrawingCanvas/Products'
import { ElevationPoints } from './ElevationPoints'
import { ElevationLines } from './ElevationLines'
import { ComfortZones } from './ComfortZones'
import { Roof } from './Roof'
import { Columns } from './Columns'
import { UtilityBoxes } from './UtilityBoxes'
import { Obstructions } from './Obstructions'
import { Walls } from './Walls'
import { deleteSelectedObjects, deselectObjects, duplicateSelectedObjects } from '~/store/selectedObjects'
import { ActionCreators } from 'redux-undo'
import { AirflowView } from './Airflow'
import { EventManager } from '~/components/DrawingCanvas/EventManager'
import { resetPanels } from '~/store/panel'
import { isLeftMouseButton } from '~/components/DrawingCanvas/util/eventHandlers'

function useKeyPress(keys: string[], shift: boolean, ctrl: boolean, callback: (event: KeyboardEvent) => void) {
  const handler = useCallback(
    (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return

      if (keys.includes(event.key) && shift === event.shiftKey && (ctrl === event.ctrlKey || ctrl === event.metaKey)) {
        event.preventDefault()
        event.stopPropagation()
        callback(event)
      }
    },
    [keys, shift, ctrl]
  )

  useEffect(() => {
    document.addEventListener('keydown', handler)
    return () => {
      document.removeEventListener('keydown', handler)
    }
  }, [handler])
}

const DELETE_KEYS = ['Backspace', 'Delete']
const DESELECT_KEYS = ['Escape']
const DUPLICATE_KEYS = ['d']
const UNDO_KEYS = ['z']
const REDO_KEYS = ['z']
const ZOOM_IN_KEYS = ['=']
const ZOOM_OUT_KEYS = ['-']
const ZOOM_RESET_KEYS = ['0']

function useKeybindings() {
  const dispatch = useAppDispatch()

  useKeyPress(DELETE_KEYS, false, false, () => {
    dispatch(deleteSelectedObjects())
  })
  useKeyPress(DESELECT_KEYS, false, false, () => {
    dispatch(deselectObjects({}))
  })
  useKeyPress(DUPLICATE_KEYS, false, true, () => {
    dispatch(duplicateSelectedObjects())
  })
  useKeyPress(UNDO_KEYS, false, true, () => {
    dispatch(ActionCreators.undo())
  })
  useKeyPress(REDO_KEYS, true, true, () => {
    dispatch(ActionCreators.redo())
  })
  useKeyPress(ZOOM_IN_KEYS, false, true, () => {
    dispatch(zoomIn())
  })
  useKeyPress(ZOOM_OUT_KEYS, false, true, () => {
    dispatch(zoomOut())
  })
  useKeyPress(ZOOM_RESET_KEYS, false, true, () => {
    dispatch(start2DFit())
  })
}

export function getCanvasState(state: RootState) {
  return {
    camera: state.camera,
    width: state.size.width,
    height: state.size.height,
  }
}

interface DrawingCanvasStore {
  spacePan: boolean
  setSpacePan(spacePan: boolean): void
}

// use zustand here instead of redux because it lets us short circuit React
// when needed for 3D performance reasons
const useDrawingCanvasStore = create<DrawingCanvasStore>(set => ({
  spacePan: false,
  setSpacePan(spacePan: boolean) {
    set(state => ({ ...state, spacePan }))
  },
}))

function useSelectorEffect<T>(
  selector: (store: AppRootState) => T,
  callback: (selected: T) => void,
  deps?: React.DependencyList
) {
  useEffect(() => {
    let lastSelected = selector(store.getState())
    return store.subscribe(() => {
      const nextSelected = selector(store.getState())
      if (lastSelected != nextSelected) {
        callback(nextSelected)
      }
      lastSelected = nextSelected
    })
  }, deps)
}

const workBox = new Box3()
// like threejs Box3#expandByObject but requires bounding boxes to be finite
// this way we can still fit the facility to the screen even when there are
// infinitely sized objects in it
function expandByObject(box: Box3, object: Object3D) {
  // Computes the world-axis-aligned bounding box of an object (including its children),
  // accounting for both the object's, and children's, world transforms
  object.updateWorldMatrix(false, false)
  // @ts-expect-error threejs-style typecheck
  const geometry = object.geometry
  if (geometry !== undefined) {
    // @ts-expect-error threejs-style typecheck
    if (object.boundingBox !== undefined) {
      // object-level bounding box
      // @ts-expect-error threejs-style typecheck
      if (object.boundingBox === null) {
        // @ts-expect-error threejs-style typecheck
        object.computeBoundingBox()
      }
      // @ts-expect-error threejs-style typecheck
      workBox.copy(object.boundingBox)
    } else {
      // geometry-level bounding box
      if (geometry.boundingBox === null) {
        geometry.computeBoundingBox()
      }
      workBox.copy(geometry.boundingBox)
    }
    workBox.applyMatrix4(object.matrixWorld)
    if (isFinite(workBox.min.x)) {
      box.union(workBox)
    }
  }

  const children = object.children
  for (let i = 0, l = children.length; i < l; i++) {
    expandByObject(box, children[i])
  }
}

const PerspControls = ({ camera }: { camera: PerspectiveCamera3 }) => {
  const dispatch = useAppDispatch()
  const controls = useRef<CameraControlsEnum | null>(null)
  const spacePan = useDrawingCanvasStore(({ spacePan }) => spacePan)

  useEffect(() => {
    const { three } = store.getState().camera

    const it = controls.current!

    it.updateCameraUp()
    it.setPosition(...three.position.location)
    it.setTarget(...three.position.target)
    it.azimuthAngle = three.rotation.azimuth
    it.polarAngle = three.rotation.polar
    requestAnimationFrame(() => {
      it.setTarget(...three.position.target)
    })

    const callback = () => {
      dispatch(set3DRotation({ polar: it.polarAngle, azimuth: it.azimuthAngle, passive: true }))
      const vec3 = new Vector3()
      it.getPosition(vec3)
      const tar3 = new Vector3()
      it.getTarget(tar3)
      const off = new Vector3()
      it.getFocalOffset(off)
      dispatch(set3DPosition({ location: vec3.toArray(), target: tar3.toArray(), passive: true }))
    }
    it.addEventListener('rest', callback)
    return () => {
      it.removeEventListener('rest', callback)
    }
  }, [camera])
  useSelectorEffect(
    store => store.camera.three,
    three => {
      if (!three.rotation.passive) {
        controls.current!.rotateTo(three.rotation.azimuth, three.rotation.polar, true)
      }
      if (three.position.passive === false) {
        controls.current!.moveTo(...three.position.location, true)
      } else if (three.position.passive === 'dolly') {
        const dolly = new Vector3(...three.position.location).distanceTo(new Vector3(...three.position.target))
        controls.current!.dollyTo(dolly, true)
      }
    }
  )

  return (
    <CameraControls
      mouseButtons={{
        left: spacePan ? CameraControlsEnum.ACTION.TRUCK : CameraControlsEnum.ACTION.NONE,
        middle: CameraControlsEnum.ACTION.TRUCK,
        right: CameraControlsEnum.ACTION.ROTATE,
        wheel: CameraControlsEnum.ACTION.DOLLY,
      }}
      touches={{
        one: CameraControlsEnum.ACTION.TOUCH_TRUCK,
        two: CameraControlsEnum.ACTION.TOUCH_DOLLY_TRUCK,
        three: CameraControlsEnum.ACTION.TOUCH_ROTATE,
      }}
      makeDefault
      minPolarAngle={0}
      maxPolarAngle={0.5 * Math.PI}
      camera={camera}
      draggingSmoothTime={0.05}
      smoothTime={0.125}
      ref={some => {
        if (some) {
          some.setBoundary(new Box3(new Vector3(-5_000, -5_000, 0), new Vector3(5_000, 5_000, 0)))
        }
        controls.current = some
      }}
      verticalDragToForward={true}
      polarRotateSpeed={0.5}
      azimuthRotateSpeed={0.5}
      dollySpeed={0.5}
      dollyToCursor={true}
      truckSpeed={1.5}
      maxDistance={3000}
      minDistance={5}
    />
  )
}

function PerspCamera() {
  const [camera, setCamera] = useState<PerspectiveCamera3 | null>(null)

  return (
    <>
      <PerspectiveCamera makeDefault fov={45} ref={setCamera} near={0.01} far={4000} up={[0, 0, 1]} />
      {camera && <PerspControls camera={camera} />}
    </>
  )
}

const OrthoControls = ({ camera, canvas }: { camera: OrthographicCamera3; canvas: MutableRefObject<Object3D> }) => {
  const dispatch = useAppDispatch()
  const [controls, setControls] = useState<CameraControlsEnum | null>(null)
  const spacePan = useDrawingCanvasStore(({ spacePan }) => spacePan)

  useEffect(() => {
    if (!controls) {
      return
    }

    const { two } = store.getState().camera

    const it = controls!

    it.zoomTo(two.zoom.factor)
    it.setLookAt(...two.position.location, 2000, ...two.position.target, 0)
    it.azimuthAngle = 0
    it.polarAngle = 0

    const callback = () => {
      const vec3 = new Vector3()
      it.getPosition(vec3)
      const [x, y] = vec3
      const tar3 = new Vector3()
      it.getTarget(tar3)
      const [tx, ty] = tar3
      dispatch(set2DPosition({ location: [x, y], target: [tx, ty], passive: true }))
      dispatch(set2DZoom({ factor: it.camera.zoom, passive: true }))
    }
    it.addEventListener('rest', callback)
    return () => {
      it.removeEventListener('rest', callback)
    }
  }, [controls])
  useSelectorEffect(
    store => store.camera.two,
    two => {
      if (!controls) {
        return
      }
      if (!two.zoom.passive) {
        controls.zoomTo(two.zoom.factor, true)
      }
      if (!two.position.passive) {
        controls.setLookAt(...two.position.location, 2000, ...two.position.target, 0, true)
      }
      const box = new Box3()
      expandByObject(box, canvas.current)
      if (two.fit && !box.isEmpty()) {
        controls.fitToBox(box, true, {
          paddingLeft: 1,
          paddingRight: 1,
          paddingTop: 1,
          paddingBottom: 1,
        })
        dispatch(fitFinished())
      }
    },
    [controls]
  )
  useEffect(() => {
    if (!controls) {
      return
    }
    const box = new Box3()
    expandByObject(box, canvas.current)
    if (!box.isEmpty()) {
      controls.fitToBox(box, false, {
        paddingLeft: 1,
        paddingRight: 1,
        paddingTop: 1,
        paddingBottom: 1,
      })
      dispatch(fitFinished())
    }
  }, [controls])

  return (
    <CameraControls
      mouseButtons={{
        left: spacePan ? CameraControlsEnum.ACTION.TRUCK : CameraControlsEnum.ACTION.NONE,
        middle: CameraControlsEnum.ACTION.TRUCK,
        right: CameraControlsEnum.ACTION.NONE,
        wheel: CameraControlsEnum.ACTION.ZOOM,
      }}
      touches={{
        one: CameraControlsEnum.ACTION.TOUCH_TRUCK,
        two: CameraControlsEnum.ACTION.TOUCH_ZOOM_TRUCK,
        three: CameraControlsEnum.ACTION.NONE,
      }}
      makeDefault
      camera={camera}
      draggingSmoothTime={0}
      polarAngle={0}
      azimuthAngle={0}
      maxPolarAngle={0}
      maxAzimuthAngle={0}
      minAzimuthAngle={0}
      minPolarAngle={0}
      maxZoom={50.0}
      minZoom={0.25}
      smoothTime={0.125}
      ref={setControls}
      verticalDragToForward={true}
      dollyToCursor={true}
    />
  )
}

function OrthoCamera({ canvas }: { canvas: MutableRefObject<Object3D> }) {
  const [camera, setCamera] = useState<OrthographicCamera3 | null>(null)

  return (
    <>
      <OrthographicCamera makeDefault ref={setCamera} near={0} far={4000} up={[0, 0, 1]} />
      {camera && <OrthoControls camera={camera} canvas={canvas} />}
    </>
  )
}

function useSpacePan(pointerInCanvas: MutableRefObject<boolean>) {
  const setSpacePan = useDrawingCanvasStore(({ setSpacePan }) => setSpacePan)

  useEffect(() => {
    const controller = new AbortController()

    window.addEventListener(
      'keydown',
      evt => {
        if (evt.key !== ' ' || !pointerInCanvas.current) {
          return
        }
        setSpacePan(true)
        document.body.classList.add('truck')
        evt.stopPropagation()
      },
      { signal: controller.signal, capture: true }
    )

    window.addEventListener(
      'keyup',
      () => {
        setSpacePan(false)
        document.body.classList.remove('truck')
      },
      { signal: controller.signal }
    )

    return () => controller.abort()
  }, [])
}

export function DrawingCanvas() {
  const is3D = useAppSelector((store: AppRootState) => store.camera.is3D)
  const dispatch = useAppDispatch()

  const toolManager = useRef<ToolManagerRef>(null)
  const canvas = useRef<Object3D>(new Object3D())

  const spacePan = useDrawingCanvasStore(({ spacePan }) => spacePan)
  const pointerInCanvas = useRef(false)

  useSpacePan(pointerInCanvas)
  useKeybindings()

  return (
    <Canvas
      onPointerMoveCapture={event => {
        if (event.target instanceof HTMLCanvasElement) {
          const isRotatingCamera = event.buttons === 2
          const isMiddleMouseTrucking = event.buttons === 4 || event.buttons === 5
          const isTruckingCamera = spacePan || isMiddleMouseTrucking
          if (toolManager.current?.onMouseMove?.(event) && !isRotatingCamera && !isTruckingCamera) {
            event.stopPropagation()
          }
        }
      }}
      onPointerDownCapture={event => {
        const isTextInput = event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement
        if (isTextInput) {
          event.stopPropagation()
          return
        }
        if (!isLeftMouseButton(event.button)) return
        const element = document.elementFromPoint(event.clientX, event.clientY)
        if (!spacePan && element instanceof HTMLCanvasElement) {
          if (toolManager.current?.onMouseDown?.(event)) {
            event.stopPropagation()
          }
        }
      }}
      onPointerUpCapture={event => {
        if (!isLeftMouseButton(event.button)) return
        if (!spacePan && event.target instanceof HTMLCanvasElement) {
          if (toolManager.current?.onMouseUp?.(event)) {
            event.stopPropagation()
          }
        }
      }}
      onPointerMissed={e => {
        const isInput = e.target instanceof HTMLInputElement
        if (!isLeftMouseButton(e.button) || isInput) return
        dispatch(deselectObjects({}))
        dispatch(resetPanels())
      }}
      onPointerEnter={() => {
        pointerInCanvas.current = true
      }}
      onPointerLeave={() => {
        pointerInCanvas.current = false
      }}
      id="drawing-canvas"
      gl={{ preserveDrawingBuffer: true, logarithmicDepthBuffer: true }}
      className="z-0"
    >
      <EventManager />
      {is3D ? <PerspCamera /> : <OrthoCamera canvas={canvas} />}
      <Grid />
      <ToolManager ref={toolManager} />
      <Lights />
      <Products />
      <AirflowView />

      <primitive object={canvas.current}>
        <MountingStructures />
        <Roof />
        <Columns />
        <Doors />
        <BackgroundImage />
        <DimensionLabels />
        <CFD />
        <Ceilings />
        <ElevationPoints />
        <ElevationLines />
        <ComfortZones />
        <UtilityBoxes />
        <Obstructions />
        <Walls />
      </primitive>
    </Canvas>
  )
}
